import { createElement } from "react";
import { createRoute, lazyRouteComponent } from "@tanstack/react-router";

import { requirePermission } from "../../../../shared/routing/requirePermission";
import {
  BRANCHES_CREATE,
  BRANCHES_UPDATE,
  BRANCHES_VIEW,
} from "../../organizationPermissions";
import { archivedBranchesSearchSchema, branchesSearchSchema } from "./searchSchemas";
import type { OrganizationRouteFactory } from "./organizationRouteContext";

const BranchesListScreen = lazyRouteComponent(
  () => import("../BranchesListScreen"),
  "BranchesListScreen",
);
const ArchivedBranchesListScreen = lazyRouteComponent(
  () => import("../ArchivedBranchesListScreen"),
  "ArchivedBranchesListScreen",
);
const BranchFormScreen = lazyRouteComponent(
  () => import("../BranchFormScreen"),
  "BranchFormScreen",
);
const BranchDetailScreen = lazyRouteComponent(
  () => import("../BranchDetailScreen"),
  "BranchDetailScreen",
);

export const createBranchesRoutes: OrganizationRouteFactory = ({
  context,
  branchManagement,
  countries,
  companies,
}) => {
  const editRoute = createRoute({
    getParentRoute: context.getParentRoute,
    path: `${context.basePath}/branches/$id/edit`,
    beforeLoad: requirePermission([BRANCHES_UPDATE]),
    component: function BranchEditRouteScreen() {
      const { id } = editRoute.useParams() as unknown as { id: string };
      return createElement(BranchFormScreen, {
        repository: branchManagement,
        countriesRepository: countries,
        companiesRepository: companies,
        basePath: context.basePath,
        branchId: id,
      });
    },
  });

  const detailRoute = createRoute({
    getParentRoute: context.getParentRoute,
    path: `${context.basePath}/branches/$id`,
    beforeLoad: requirePermission([BRANCHES_VIEW]),
    component: function BranchDetailRouteScreen() {
      const { id } = detailRoute.useParams() as unknown as { id: string };
      return createElement(BranchDetailScreen, {
        repository: branchManagement,
        basePath: context.basePath,
        branchId: id,
      });
    },
  });

  return [
    createRoute({
      getParentRoute: context.getParentRoute,
      path: `${context.basePath}/branches`,
      validateSearch: branchesSearchSchema,
      beforeLoad: requirePermission([BRANCHES_VIEW]),
      component: () =>
        createElement(BranchesListScreen, {
          repository: branchManagement,
          basePath: context.basePath,
        }),
    }),
    createRoute({
      getParentRoute: context.getParentRoute,
      path: `${context.basePath}/branches/create`,
      beforeLoad: requirePermission([BRANCHES_CREATE]),
      component: () =>
        createElement(BranchFormScreen, {
          repository: branchManagement,
          countriesRepository: countries,
          companiesRepository: companies,
          basePath: context.basePath,
        }),
    }),
    createRoute({
      getParentRoute: context.getParentRoute,
      path: `${context.basePath}/branches/archived`,
      validateSearch: archivedBranchesSearchSchema,
      beforeLoad: requirePermission([BRANCHES_VIEW]),
      component: () =>
        createElement(ArchivedBranchesListScreen, {
          repository: branchManagement,
          basePath: context.basePath,
        }),
    }),
    editRoute,
    detailRoute,
  ];
};
